import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useDispatch} from 'react-redux';
import {useMediaQuery, useTheme} from '@mui/material';
import {flowActions} from '../../store/actions/flowAction';
import {flowServices} from '../../service/flowService';
import {campaignFormActions} from '../../store/actions/campaignFormAction';
import {alertActions} from '../../store/actions/alertActions';
import {countries} from '../utils/DataList';
import FlowForm from './FlowForm';
import {FlowDialog} from './FlowDesign';

export function getFlowValidationError(flow) {
    for (const rule of flow.rules) {
        for (const path of rule.paths) {
            if (!path.status) continue;
            const missingOffer = path.offers.some(offer => !offer.offerId);
            const missingLanding = !path.directLinking && path.landings.some(landing => !landing.landingId);
            if (missingOffer || missingLanding) return `Select the missing ${missingOffer && missingLanding ? 'offers and landings' : missingOffer ? 'offers' : 'landings'} in “${rule.name}” → “${path.name}”.`;
        }
    }
    return '';
}

export default function FlowEditor({mode = 'add', history, location, match}) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(mode !== 'add');
    const [loadFailed, setLoadFailed] = useState(false);
    const [requestError, setRequestError] = useState('');
    const [flowData, setFlowData] = useState(null);
    const [open, setOpen] = useState(true);
    const {register, handleSubmit, control, getValues, setValue, watch, formState: {errors}} = useForm({shouldUnregister: true});
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const id = match?.params?.id;
    useEffect(() => {
        if (mode === 'add') return;
        let active = true;
        setLoading(true);
        flowServices.find(id).then(record => {
            if (!active) return;
            if (!(record?.id > 0)) throw new Error('Flow not found.');
            const flow = typeof record.flow === 'string' ? JSON.parse(record.flow) : record.flow;
            setFlowData(record);
            setValue('name', record.name + (mode === 'duplicate' ? ' - [copy]' : ''));
            setValue('tags', record.tags || []);
            setValue('country', countries.find(country => country.code === record.countryCode) || countries.find(country => country.code === ''));
            dispatch(campaignFormActions.setFlow(flow));
            dispatch(campaignFormActions.updateSelectedPath(0, 0, flow.rules[0].paths[0]));
        }).catch(error => {
            if (active) {setLoadFailed(true); setRequestError(error?.response?.data?.error || 'Unable to load this flow. Close and reopen to try again.');}
        }).finally(() => {if (active) setLoading(false);});
        return () => {active = false;};
    }, [mode, id, setValue, dispatch]);
    const handleClose = (event, reason) => {
        if (reason === 'backdropClick' || loading) return;
        dispatch(campaignFormActions.resetForm());
        dispatch(alertActions.clear());
        setOpen(false);
        history.push({pathname: '/flows', state: {from: location}});
    };
    const onSubmit = async ({data, flow}) => {
        const validationError = getFlowValidationError(flow);
        if (validationError) {setRequestError(validationError); return;}
        setRequestError('');
        setLoading(true);
        const payload = {name: data.name, tags: data.tags || [], flow,
            countryCode: data.country?.code || '', workspaceId: data.workspace?.value};
        try {
            const result = mode === 'edit' ? await flowServices.update(id, payload) : await flowServices.create(payload);
            if (!(result?.id > 0)) throw new Error();
            dispatch(campaignFormActions.resetForm());
            dispatch(alertActions.clear());
            dispatch(flowActions.rerender(true));
            setOpen(false);
            history.push({pathname: '/flows', state: {from: location}});
        } catch (error) {
            setRequestError(error?.response?.data?.error || 'Unable to save this flow. Please try again.');
        } finally {setLoading(false);}
    };
    return <FlowDialog open={open} onClose={handleClose} fullWidth fullScreen={fullScreen} maxWidth="lg" aria-labelledby="flow-form-title">
        <FlowForm title={mode === 'add' ? 'New flow' : mode === 'edit' ? 'Update flow' : 'Duplicate flow'}
            submitLabel={mode === 'add' ? 'Create flow' : mode === 'edit' ? 'Save changes' : 'Create copy'}
            control={control} register={register} handleClose={handleClose} errors={errors} handleSubmit={handleSubmit}
            onSubmit={onSubmit} loading={loading} flowData={flowData} getValues={getValues} setValue={setValue} watch={watch}
            requestError={requestError} saveDisabled={loadFailed}/>
    </FlowDialog>;
}
